"use client";

import type { ReactNode } from "react";

import { ChevronDownIcon } from "@heroicons/react/24/outline";
import * as Accordion from "@radix-ui/react-accordion";
import { Separator } from "@radix-ui/themes";
import clsx from "clsx";

export interface CollapsibleAccordionProps {
  readonly items: {
    content: ReactNode;
    header: ReactNode;
    id: number;
  }[];
}

export function CollapsibleAccordion({
  items,
}: CollapsibleAccordionProps): ReactNode {
  if (items.length === 0) {
    return null;
  }
  return (
    <Accordion.Root collapsible type="single">
      <Separator size="4" />
      {items.map(({ content, header, id }) => (
        <Accordion.Item
          className="overflow-hidden"
          key={id.toString()}
          value={id.toString()}
        >
          <Accordion.Header asChild>
            <Accordion.Trigger className="group flex w-full cursor-pointer items-center gap-1 py-4">
              <div className="font-bold-antialiased flex-1 text-left">
                {header}
              </div>
              <ChevronDownIcon
                className="size-5 shrink-0 transition-transform duration-300 ease-[cubic-bezier(0.87,0,0.13,1)] will-change-transform group-data-[state=open]:rotate-180"
                strokeWidth={2.5}
              />
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content
            className={clsx(
              "will-change-[height]",
              `data-[state=closed]:animate-[accordionSlideUp_300ms_cubic-bezier(0.87,0,0.13,1)]`,
              `data-[state=open]:animate-[accordionSlideDown_300ms_cubic-bezier(0.87,0,0.13,1)]`,
            )}
          >
            <div className="pb-4">{content}</div>
          </Accordion.Content>
          <Separator size="4" />
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}
